import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import {
  RiBookOpenLine,
  RiCheckLine,
  RiInformationLine,
  RiLiveLine,
  RiShoppingBag3Line,
} from "@remixicon/react";
import type { ComponentType } from "react";

export const FEED_COMMENT_ORDER_FORMAT_ROWS: ReadonlyArray<{
  example: string;
  meaning: string;
}> = [
  { example: "A1", meaning: "ລະຫັດ A1 · ຈຳນວນ 1 (ຄ່າເລີ່ມຕົ້ນ)" },
  { example: "A1=3", meaning: "ລະຫັດ A1 · ຈຳນວນ 3" },
  { example: "A1 = 1", meaning: "ລະຫັດ A1 · ຈຳນວນ 1 (ມີຊ່ອງຫວ່າງໄດ້)" },
  { example: "A1/3", meaning: "ລະຫັດ A1 · ຈຳນວນ 3" },
  { example: "A1 3", meaning: "ລະຫັດ A1 · ຈຳນວນ 3 (ຄັ່ນດ້ວຍຊ່ອງຫວ່າງ)" },
  { example: "a1=2", meaning: "ປ່ຽນເປັນ A1 · ຈຳນວນ 2 (ຕົວພິມນ້ອຍ/ໃຫຍ່ຄືກັນ)" },
  {
    example: "A1=2 AAF/3",
    meaning: "ສອງລາຍການ: A1 ຈຳນວນ 2 ແລະ AAF ຈຳນວນ 3",
  },
];

/** ตารางรูปแบบ CF ชุดเดียวกันสำหรับหน้า Guide และหน้าจอพนักงาน Live */
export function FeedCommentOrderFormatTable() {
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <Table>
        <TableHeader className="bg-muted/40">
          <TableRow>
            <TableHead className="w-[34%] text-xs">ຕົວຢ່າງຄອມເມັນ</TableHead>
            <TableHead className="text-xs">ຄວາມໝາຍ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {FEED_COMMENT_ORDER_FORMAT_ROWS.map((row) => (
            <TableRow key={row.example}>
              <TableCell className="align-top">
                <Badge variant="secondary" className="font-mono">
                  {row.example}
                </Badge>
              </TableCell>
              <TableCell className="min-w-64 text-muted-foreground">
                {row.meaning}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function FeedCommentOrderGuide() {
  return (
    <div className="space-y-8 text-sm">
      <div className="flex gap-3 rounded-xl border bg-primary/5 p-5">
        <RiInformationLine className="mt-0.5 size-5 shrink-0 text-primary" />
        <div className="min-w-0 space-y-2">
          <p className="font-semibold text-base">ລະບົບອ່ານຈຳນວນຕົງຕາມທີ່ລູກຄ້າພິມ</p>
          <p className="text-muted-foreground leading-relaxed">
            ຕົວຢ່າງ A1=3 ຈະສ້າງຄຳສັ່ງ A1 ຈຳນວນ 3 ຊິ້ນ ບໍ່ແມ່ນ 1 ຊິ້ນ. ລະບົບຈະສ້າງອໍເດີ້ເມື່ອລະຫັດກົງ,
            ໂພສເປີດຂາຍ ແລະຍັງມີສິນຄ້າ.
          </p>
        </div>
      </div>

      <section className="space-y-4">
        <div className="space-y-1">
          <h3 className="font-semibold text-lg">ຂັ້ນຕອນການເຮັດວຽກ</h3>
          <p className="text-muted-foreground">ຈາກຄອມເມັນຫາອໍເດີ້ໃນ 3 ຂັ້ນຕອນ</p>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <GuideStep
            icon={RiLiveLine}
            number="1"
            title="ລູກຄ້າພິມລະຫັດ"
            description="ພິມລະຫັດ CF ແລະຈຳນວນໃນຄອມເມັນຂອງໂພສ / Live ນີ້"
          />
          <GuideStep
            icon={RiCheckLine}
            number="2"
            title="ລະບົບກວດສອບ"
            description="ກວດລະຫັດ, ຈຳນວນ, ສະຖານະການຂາຍ ແລະສິນຄ້າຄົງເຫຼືອ"
          />
          <GuideStep
            icon={RiShoppingBag3Line}
            number="3"
            title="ສ້າງອໍເດີ້"
            description="ບັນທຶກຈຳນວນທີ່ຜ່ານການກວດສອບ ແລະແຈ້ງສະຖານະໃຫ້ພະນັກງານ"
          />
        </div>
      </section>

      <section className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)]">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <RiBookOpenLine className="size-5 text-primary" />
              ຮູບແບບທີ່ໃຊ້ໄດ້
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              ເລືອກໃຊ້ຮູບແບບໃດຮູບແບບໜຶ່ງໄດ້
            </p>
          </CardHeader>
          <CardContent>
            <FeedCommentOrderFormatTable />
          </CardContent>
        </Card>

        <div className="space-y-4">
          <GuideRuleCard
            title="ກົດລະຫັດ CF"
            items={[
              "ໃຊ້ຕົວອັກສອນ A–Z, ຕົວເລກ 0–9 ແລະເຄື່ອງໝາຍ -",
              "ລະຫັດຍາວ 1–10 ຕົວອັກສອນ",
              "ລະຫັດຕ້ອງກົງກັບສິນຄ້າໃນແທັບ ສິນຄ້າ",
            ]}
          />
          <GuideRuleCard
            title="ເງື່ອນໄຂກ່ອນສ້າງອໍເດີ້"
            items={[
              "ໂພສເປີດຮັບຄຳສັ່ງແລ້ວ",
              "session ຍັງເປີດ ແລະສິນຄ້າເປີດຂາຍ",
              "ຄອມເມັນຢູ່ໂພສ / Live ນີ້ໂດຍກົງ",
            ]}
          />
        </div>
      </section>

      <Card className="border-destructive/30 bg-destructive/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">ກໍລະນີທີ່ບໍ່ສ້າງອໍເດີ້</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="grid gap-2 text-muted-foreground md:grid-cols-3">
            <li>ບໍ່ມີລະຫັດ CF ໃນຄອມເມັນ</li>
            <li>ລະຫັດບໍ່ມີໃນໂພສ ຫຼື ສິນຄ້າປິດຂາຍ</li>
            <li>ໂພສ ຫຼື session ປິດແລ້ວ</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function GuideStep({
  icon: Icon,
  number,
  title,
  description,
}: {
  icon: ComponentType<{ className?: string }>;
  number: string;
  title: string;
  description: string;
}) {
  return (
    <Card className="h-full">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center justify-between">
          <span className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon className="size-5" />
          </span>
          <span className="font-mono text-muted-foreground text-xs">
            {number.padStart(2, "0")}
          </span>
        </div>
        <div className="space-y-1">
          <p className="font-semibold">{title}</p>
          <p className="text-muted-foreground leading-relaxed">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function GuideRuleCard({ title, items }: { title: string; items: string[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3 text-muted-foreground">
          {items.map((item) => (
            <li key={item} className="flex gap-2">
              <RiCheckLine className="mt-0.5 size-4 shrink-0 text-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
